import React from 'react';
import PropTypes from 'prop-types';
import { Panel } from 'react-bootstrap';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import camelcaseKeys from 'camelcase-keys';
import sortBy from 'lodash/sortBy';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDoubleRight, faPlus } from '@fortawesome/pro-solid-svg-icons';

import LoadingSpinner from 'components/loading_spinner';
import TaskList from 'components/tasks/task_list';
import EmptyState from 'components/project/empty_state';
import PageHeader from 'components/project/page_header';
import {
  addTask,
  removeTask,
  toggleTaskCompleted,
  updateTask
} from 'bundles/project/actions/index';

const NUM_TO_DISPLAY = 7;

export class OpenTasks extends React.Component {
  static propTypes = {
    addTask: PropTypes.func.isRequired,
    availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
    databaseMembers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })).isRequired,
    defaultAssociatedModel: PropTypes.shape({
      associatedModelId: PropTypes.number.isRequired,
      associatedModelType: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      default: PropTypes.bool.isRequired
    }).isRequired,
    fetching: PropTypes.bool.isRequired,
    hideContactSelect: PropTypes.bool,
    hidePropertySelect: PropTypes.bool,
    openTasks: PropTypes.arrayOf(PropTypes.object).isRequired,
    projectId: PropTypes.number.isRequired,
    removeTask: PropTypes.func.isRequired,
    showNewTaskModal: PropTypes.func.isRequired,
    toggleTaskCompleted: PropTypes.func.isRequired,
    updateTask: PropTypes.func.isRequired
  }

  onNewTaskClicked = (e) => {
    e.preventDefault();
    this.props.showNewTaskModal();
  }

  renderAddTaskCTA() {
    if (this.props.fetching) return;

    return (
      <a className="pr-5" href="#" key="add-link" name="add-task" onClick={this.onNewTaskClicked}>
        <FontAwesomeIcon icon={faPlus} size="sm"/> Task
      </a>
    );
  }

  renderMoreTasksCTA() {
    return (
      <Link key="task-tab-link" name="task-tab-link" to="/project_tasks">
        <FontAwesomeIcon icon={faAngleDoubleRight}/> More
      </Link>
    );
  }

  render() {
    return (
      <React.Fragment>
        {this.renderHeader()}
        {this.renderTasks()}
      </React.Fragment>
    );
  }

  renderHeader() {
    const subtitle = !this.props.fetching ? ` - ${pluralize('open task', this.props.openTasks.length, true)}` : '';
    const actions = (
      <React.Fragment>
        {this.renderAddTaskCTA()}
        {this.renderMoreTasksCTA()}
      </React.Fragment>
    );

    return (
      <div className="px-3 my-4">
        <PageHeader
          actions={actions}
          subtitle={subtitle}
          title="Tasks"
        />
      </div>
    );
  }

  tasksForDisplay() {
    const openTasks = sortBy(this.props.openTasks, x => x.dueDate);
    return openTasks.slice(0, NUM_TO_DISPLAY);
  }

  renderTasks() {
    const { fetching, openTasks } = this.props;

    if (fetching) {
      return (
        <div className="centered-image">
          <LoadingSpinner />
        </div>
      );
    }

    if (openTasks.length === 0) {
      return (
        <Panel>
          <Panel.Body className="mt-2 p-0">
            <EmptyState imageSrc="/images/project_empty_states/tasks.svg" message="No Tasks to display." />
          </Panel.Body>
        </Panel>
      );
    }

    return (
      <div className="panel panel-default panel-overview-tasks">
        <table className="table table-condensed no-outer-border w-100">
          <tbody>
            <TaskList
              availableDatabases={this.props.availableDatabaseOptions}
              databaseMembers={this.props.databaseMembers}
              defaultAssociatedModels={[this.props.defaultAssociatedModel]}
              hideActions
              hideAssociations
              hideContactSelect={this.props.hideContactSelect}
              hidePropertySelect={this.props.hidePropertySelect}
              multipleProjectsInTask={true}
              projectId={this.props.projectId}
              tasks={this.tasksForDisplay()}
              onDeleteTask={this.props.removeTask}
              onSaveTask={this.props.addTask}
              onToggleTaskState={this.props.toggleTaskCompleted}
              onUpdateTask={this.props.updateTask}
            />
          </tbody>
        </table>
      </div>
    );
  }
}

const mapStateToProps = ({ availableDatabaseOptions, project, taskOptions, tasks }) => ({
  availableDatabaseOptions,
  databaseMembers: [...project.collaborators, ...project.otherAuthorizedUsers],
  defaultAssociatedModel: project.defaultTaskAssociation,
  fetching: !!tasks.fetching,
  hideContactSelect: taskOptions.hideContactSelect,
  hidePropertySelect: taskOptions.hidePropertySelect,
  openTasks: tasks.fetching ? [] : camelcaseKeys(tasks.filter(task => !task.completed), { deep: true }),
  projectId: project.id
});

const actions = {
  addTask,
  updateTask,
  toggleTaskCompleted,
  removeTask
};

export default connect(mapStateToProps, actions)(OpenTasks);
